/**
 * The WYSIWYG two-page spread canvas: drag, resize, layer, and edit
 * elements in place. Ported from the design prototype's
 * spread-editor/canvas.jsx ("Stage" direction).
 *
 * Element chrome (toolbar, popover menus, resize handles, selection
 * outline) renders in a dedicated .cel-chrome overlay ABOVE every canvas
 * element rather than inside the element's own stacking context — the
 * inline zIndex each .cel carries would otherwise paint higher-stacked
 * art over a lower element's toolbar.
 */
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import type {
  FormatPreset,
  SpreadElement,
  SpreadTextElement,
} from '@historyjki/shared';
import {
  TEXT_REFINE_OPTIONS,
  TEXT_EFFECT_OPTIONS,
  IMAGE_EDGE_OPTIONS,
  IMAGE_SHAPE_OPTIONS,
  SPREAD_DESIGN_W,
  SPREAD_TEXT_PANEL_PAD,
  SAFE_MARGIN_MM,
  checkElementPlacement,
  clampTextBoxToSafeArea,
  classifySharpness,
  effectiveImageDpi,
  shrinkToSharpFactor,
  textOutsetPct,
  type PlacementWarning,
  type SharpnessLevel,
} from '@historyjki/shared';
import type { SpreadImageShape } from '@historyjki/shared';
import {
  DEFAULT_SHAPE_TRANSFORM,
  SPREAD_TEXT_COLORS,
  SPREAD_TEXT_EFFECTS,
  SPREAD_TEXT_LINE_HEIGHT,
  computeWrapExclusions,
  getCanvasSizePt,
  rgbaCss,
  sortedElements,
} from '../../utils/spreadGeometry';
import { imageEdgeCss, imageShapeCss } from '../../utils/imageEdgeCss';
import { useImageDims } from '../../utils/imageDims';
import { r2Url } from '../../utils/r2Url';
import {
  canvasTextMeasure,
  flowText,
  isBookFontLoaded,
  loadBookFont,
} from '../../utils/textFlow';
import type { ComposeStore, SpreadView, BankTile } from '../../hooks/useComposeStage';
import { Spinner } from '../ui/Spinner';
import { SpreadImage } from './SpreadImage';
import { CropOverlay } from './CropOverlay';
import { ShapeOverlay } from './ShapeOverlay';
import {
  IconImage,
  IconAlign,
  IconWarning,
  IconForward,
  IconBackward,
  IconCopy,
  IconTrash,
  IconWand,
  IconContrast,
  IconReadable,
  IconEdges,
  IconCrop,
  IconShape,
  IconTextFlow,
} from './icons';

/** The project's book typography, applied to every text block so the
 *  canvas matches the PDF's font face and weight exactly. */
export interface BookFont {
  family: string;
  weight: number;
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/** 1 design unit in canvas-width-relative cqw (same rule as font sizes). */
const DESIGN_UNIT_CQW = 100 / SPREAD_DESIGN_W;

/** Aspect ratio of the edge menu's preview tiles (must match the CSS). */
const EDGE_PREVIEW_AR = 64 / 44;

/** Editor-only tooltip for the print-sharpness badge. Plain language on
 *  purpose — the fix matters ("shrink or regenerate bigger"), not dpi. */
function sharpnessTitle(level: SharpnessLevel): string {
  if (level === 'blurry') {
    return (
      'At this size this image will print visibly blurry. Shrink it on the ' +
      'page (the toolbar has a one-tap fix), or generate a take at a bigger ' +
      'size preset.'
    );
  }
  return (
    'At this size this image will print slightly soft. Shrink it a little ' +
    '(the toolbar has a one-tap fix), or generate a take at a bigger size ' +
    'preset.'
  );
}

/** Print sharpness of a placed image at its current size, from the
 *  image's cached natural pixels. Null while dims load, for text
 *  elements, and for placements that print sharp. */
function useSharpness(
  el: SpreadElement,
  store: ComposeStore,
): { level: SharpnessLevel; dpi: number } | null {
  const dims = useImageDims(el.type === 'image' ? el.r2_key : null);
  if (el.type !== 'image' || !dims) return null;
  const dpi = effectiveImageDpi(
    el,
    dims.w,
    dims.h,
    store.formatPreset,
    store.current?.kind ?? 'spread',
  );
  const level = classifySharpness(dpi);
  return level === 'sharp' ? null : { level, dpi };
}

/** Editor-only tooltip for a placement warning — never enters the PDF. */
function placementWarningTitle(w: PlacementWarning): string {
  if (w.kind === 'image-dead-zone') {
    const edges = w.edges?.join(', ') ?? '';
    return (
      `This image stops just short of the page edge (${edges}). ` +
      'Trimming can shift up to ±1.5 mm, so a thin white strip or an uneven ' +
      'margin may show in print. Drag the image past the page edge, or move ' +
      'it clearly inside the page.'
    );
  }
  if (w.gutter) {
    return (
      'This text sits in the gutter fold area — the binding can swallow it. ' +
      'Move it fully onto one page, clear of the fold.'
    );
  }
  return (
    `This text sits within ${SAFE_MARGIN_MM} mm of the page edge and may be ` +
    'cut off when the book is trimmed. Move it toward the middle of the page.'
  );
}

/**
 * Readability treatment for a text block as CSS. Halo is a blur-free
 * 8-direction outline and shadow a single hard offset — both expressed in
 * em so they scale with the font size and match the PDF's offset draws.
 */
function textEffectStyle(t: SpreadTextElement): CSSProperties {
  const effect = t.effect ?? 'none';
  if (effect === 'halo') {
    const def = SPREAD_TEXT_EFFECTS.halo;
    const c = rgbaCss((def.byColor[t.color] ?? def.byColor.ink).rgb, 1);
    const d = `${def.ratio}em`;
    return {
      textShadow: [
        `${d} 0 0 ${c}`,
        `-${d} 0 0 ${c}`,
        `0 ${d} 0 ${c}`,
        `0 -${d} 0 ${c}`,
        `${d} ${d} 0 ${c}`,
        `${d} -${d} 0 ${c}`,
        `-${d} ${d} 0 ${c}`,
        `-${d} -${d} 0 ${c}`,
      ].join(', '),
    };
  }
  if (effect === 'shadow') {
    const def = SPREAD_TEXT_EFFECTS.shadow;
    const d = `${def.ratio}em`;
    return { textShadow: `${d} ${d} 0 ${rgbaCss(def.rgb, def.alpha)}` };
  }
  return {};
}

/**
 * The translucent panel behind a text block. Expands OUTWARD from the
 * element box so text wrapping/position are untouched — the PDF draws the
 * same expanded rectangle behind the same box.
 */
function TextPanel({ el }: { el: SpreadTextElement }) {
  if ((el.effect ?? 'none') !== 'panel') return null;
  const def = SPREAD_TEXT_EFFECTS.panel;
  const c = def.byColor[el.color] ?? def.byColor.ink;
  return (
    <span
      className="cel__panel"
      style={{
        inset: `${-def.pad * DESIGN_UNIT_CQW}cqw`,
        background: rgbaCss(c.rgb, c.alpha),
        borderRadius: `${def.radius * DESIGN_UNIT_CQW}cqw`,
      }}
    />
  );
}

/** Font/paint style shared by the editor canvas and StaticSpread. */
function textStyle(t: SpreadTextElement, bookFont: BookFont): CSSProperties {
  return {
    ['--els' as string]: t.size,
    fontFamily: bookFont.family,
    fontWeight: bookFont.weight,
    textAlign: t.align,
    color: SPREAD_TEXT_COLORS[t.color]?.css ?? SPREAD_TEXT_COLORS.ink.css,
    ...textEffectStyle(t),
  };
}

/** True once the book font face can be measured; kicks the load when it
 *  can't yet. Layout through a fallback face would compute breaks the
 *  real glyphs then contradict, so FlowedText waits on this. */
function useBookFontReady(bookFont: BookFont, needed: boolean): boolean {
  const [, bump] = useReducer((n: number) => n + 1, 0);
  const ready = needed && isBookFontLoaded(bookFont.family, bookFont.weight);
  useEffect(() => {
    if (!needed || ready) return;
    let cancelled = false;
    void loadBookFont(bookFont.family, bookFont.weight).then(() => {
      if (!cancelled) bump();
    });
    return () => {
      cancelled = true;
    };
  }, [needed, ready, bookFont.family, bookFont.weight]);
  return ready;
}

/**
 * A text block's content, flowing around any text_wrap image silhouettes
 * that reach it. With no exclusions (or while the font face is still
 * loading) it renders the pre-existing plain block — browser wrapping,
 * byte-identical DOM — so spreads without wrap are untouched. With
 * exclusions it lays lines out through the shared flowText engine and
 * paints them as absolutely positioned runs; the PDF exporter runs the
 * SAME engine with the SAME canvas measure, so print matches the canvas
 * to the line break.
 */
function FlowedText({
  el,
  elements,
  canvasAspect,
  bookFont,
}: {
  el: SpreadTextElement;
  elements: SpreadElement[];
  canvasAspect: number;
  bookFont: BookFont;
}) {
  const exclusions = useMemo(
    () => computeWrapExclusions(el, elements, canvasAspect),
    [el, elements, canvasAspect],
  );
  const fontReady = useBookFontReady(bookFont, exclusions.length > 0);

  const designH = SPREAD_DESIGN_W / canvasAspect;
  const boxW = (el.w / 100) * SPREAD_DESIGN_W;
  const boxH = (el.h / 100) * designH;

  const runs = useMemo(() => {
    if (exclusions.length === 0 || !fontReady) return null;
    const measure = canvasTextMeasure(bookFont.family, bookFont.weight, el.size);
    if (!measure) return null;
    return flowText({
      text: el.text,
      size: el.size,
      lineHeight: el.size * SPREAD_TEXT_LINE_HEIGHT,
      boxW,
      boxH,
      align: el.align,
      exclusions,
      measure,
    });
  }, [exclusions, fontReady, bookFont.family, bookFont.weight, el.text, el.size, el.align, boxW, boxH]);

  const style = textStyle(el, bookFont);
  if (!runs) {
    return (
      <div className="cel__text" style={style}>
        {el.text}
      </div>
    );
  }
  return (
    <div className="cel__text cel__text--flow" style={style} data-testid="cel-text-flow">
      {runs.map((r, i) => (
        <span
          key={i}
          className="cel__flowrun"
          style={{ left: `${(r.x / boxW) * 100}%`, top: `${(r.top / boxH) * 100}%` }}
        >
          {r.text}
        </span>
      ))}
    </div>
  );
}

/** Every non-rectangular edge or cutout shape fades/erodes the box — a
 *  hard box-shadow line would betray it. */
const softEdgeClass = (el: SpreadElement) =>
  el.type === 'image' &&
  (el.shape ||
    (el.edge && el.edge !== 'sharp' && el.edge !== 'rounded'))
    ? 'cel--softedge'
    : '';

/**
 * Pointer-driven drag of a bank tile onto any visible spread canvas. A
 * ghost follows the pointer; dropping over `[data-spread-canvas]` places
 * the image at the pointer's canvas-relative position.
 */
export function startBankDrag(
  e: ReactPointerEvent,
  tile: BankTile,
  addImageAt: (tile: BankTile, x: number, y: number) => void,
  onDone?: () => void,
): void {
  if (!tile.r2_key) return;
  e.preventDefault();
  const ghost = document.createElement('div');
  ghost.className = 'drag-ghost';
  ghost.textContent = tile.caption || 'image';
  ghost.style.backgroundImage = `url("${r2Url(tile.r2_key)}")`;
  document.body.appendChild(ghost);

  const move = (ev: PointerEvent) => {
    ghost.style.left = `${ev.clientX}px`;
    ghost.style.top = `${ev.clientY}px`;
    const over = document.elementFromPoint(ev.clientX, ev.clientY);
    const canvas = over?.closest('[data-spread-canvas]');
    document.querySelectorAll('[data-spread-canvas]').forEach((c) => {
      c.classList.toggle('spread--drop', c === canvas);
    });
  };
  const up = (ev: PointerEvent) => {
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', up);
    ghost.remove();
    const over = document.elementFromPoint(ev.clientX, ev.clientY);
    const canvas = over?.closest('[data-spread-canvas]');
    document.querySelectorAll('[data-spread-canvas]').forEach((c) => {
      c.classList.remove('spread--drop');
    });
    if (canvas) {
      const r = canvas.getBoundingClientRect();
      const x = ((ev.clientX - r.left) / r.width) * 100;
      const y = ((ev.clientY - r.top) / r.height) * 100;
      addImageAt(tile, x, y);
      onDone?.();
    }
  };
  move(e.nativeEvent);
  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', up);
}

type OpenMenu = 'refine' | 'effect' | 'edge' | 'shape' | null;

interface CanvasElementProps {
  el: SpreadElement;
  active: boolean;
  editing: boolean;
  onStartEditing: (elId: string) => void;
  onEndEditing: () => void;
  canvasRef: React.RefObject<HTMLDivElement | null>;
  store: ComposeStore;
  bookFont: BookFont;
}

/** One placed element: content + drag/edit interactions. Its chrome
 *  (toolbar/menus/handles) lives in ElementChrome, painted above all. */
function CanvasElement({
  el,
  active,
  editing,
  onStartEditing,
  onEndEditing,
  canvasRef,
  store,
  bookFont,
}: CanvasElementProps) {
  const { updateEl, setSelectedEl } = store;

  const rectPct = (dxPx: number, dyPx: number): [number, number] => {
    const r = canvasRef.current?.getBoundingClientRect();
    if (!r) return [0, 0];
    return [(dxPx / r.width) * 100, (dyPx / r.height) * 100];
  };

  const spreadKind = store.current?.kind ?? 'spread';

  const onDragStart = (e: ReactPointerEvent) => {
    if (editing) return;
    e.stopPropagation();
    setSelectedEl(el.id);
    const sx = e.clientX;
    const sy = e.clientY;
    const ox = el.x;
    const oy = el.y;
    const move = (ev: PointerEvent) => {
      const [dx, dy] = rectPct(ev.clientX - sx, ev.clientY - sy);
      if (el.type === 'text') {
        // Text is hard-clamped into the print-safe area (trim margins +
        // gutter fold); only images may ride or cross the page edge.
        updateEl(
          el.id,
          clampTextBoxToSafeArea(
            { x: ox + dx, y: oy + dy, w: el.w, h: el.h },
            store.formatPreset,
            spreadKind,
            textOutsetPct(el, store.formatPreset, spreadKind, SPREAD_TEXT_PANEL_PAD),
          ),
        );
      } else {
        updateEl(el.id, {
          x: clamp(ox + dx, -8, 100 - el.w + 8),
          y: clamp(oy + dy, -8, 100 - el.h + 8),
        });
      }
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const style: CSSProperties = {
    left: `${el.x}%`,
    top: `${el.y}%`,
    width: `${el.w}%`,
    height: `${el.h}%`,
    zIndex: el.z,
  };

  // Print-safety warning icon — derived state, editor canvas only. It is
  // never stored on the element and the PDF renderer never sees it.
  const warning = checkElementPlacement(
    el,
    store.formatPreset,
    spreadKind,
    SPREAD_TEXT_PANEL_PAD,
  );

  // Print-sharpness chip — same derived-state rules as the warning icon.
  const sharpness = useSharpness(el, store);

  return (
    <div
      className={`cel cel--${el.type} ${active ? 'cel--active' : ''} ${softEdgeClass(el)}`}
      style={style}
      data-testid={`cel-${el.type}`}
      onPointerDown={onDragStart}
      onClick={(e) => {
        e.stopPropagation();
        setSelectedEl(el.id);
      }}
      onDoubleClick={(e) => {
        if (el.type === 'text') {
          e.stopPropagation();
          onStartEditing(el.id);
        }
      }}
    >
      {el.type === 'image' ? (
        <SpreadImage el={el} boxAspect={(el.w / el.h) * store.canvasAspect} />
      ) : editing ? (
        <textarea
          autoFocus
          className="cel__textarea"
          defaultValue={el.text}
          style={{ ...textStyle(el, bookFont), textShadow: 'none' }}
          onBlur={(e) => {
            updateEl(el.id, { text: e.target.value });
            onEndEditing();
          }}
          onPointerDown={(e) => e.stopPropagation()}
        />
      ) : (
        <>
          <TextPanel el={el} />
          <FlowedText
            el={el}
            elements={store.current?.elements ?? []}
            canvasAspect={store.canvasAspect}
            bookFont={bookFont}
          />
        </>
      )}
      {warning && (
        <span
          className="cel__warn"
          title={placementWarningTitle(warning)}
          data-testid="cel-warning"
          data-warning-kind={warning.kind}
        >
          <IconWarning />
        </span>
      )}
      {sharpness && (
        <span
          className={`cel__sharp cel__sharp--${sharpness.level}`}
          title={sharpnessTitle(sharpness.level)}
          data-testid="cel-sharpness"
          data-sharpness={sharpness.level}
        >
          {sharpness.level === 'blurry' ? 'prints blurry' : 'prints soft'}
        </span>
      )}
    </div>
  );
}

interface ElementChromeProps {
  el: SpreadElement;
  canvasRef: React.RefObject<HTMLDivElement | null>;
  store: ComposeStore;
}

/**
 * The selected element's floating chrome: selection outline, resize
 * handles, the options toolbar, and its popover menus. Rendered as the
 * spread's LAST child in a box that mirrors the element's, with a
 * z-index above every element (and the gutter/folio overlays), so the
 * options always paint on top of the whole stack.
 */
function ElementChrome({ el, canvasRef, store }: ElementChromeProps) {
  const { updateEl, removeEl, duplicateEl, reorderEl, refineText, refiningElId } = store;
  const [menu, setMenu] = useState<OpenMenu>(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [shapeAdjust, setShapeAdjust] = useState(false);

  const pickShape = (kind: string) => {
    setMenu(null);
    if (kind === 'none') {
      setShapeAdjust(false);
      updateEl(el.id, { shape: null });
      return;
    }
    // Switching kinds keeps the placement the author already dialed in.
    // Any edge softening stays — it stacks onto the cut silhouette.
    const prev = el.type === 'image' ? el.shape : null;
    const shape = {
      ...(prev ?? DEFAULT_SHAPE_TRANSFORM),
      kind,
    } as SpreadImageShape;
    updateEl(el.id, { shape });
    setShapeAdjust(true);
  };

  // ── Keep the floating chrome over the spread ──────────────────────────
  // The chrome layer isn't clipped by the page (see .spread--editor), but
  // it should still hug the canvas. Vertical: flip the bar below the
  // element near the top edge; open menus toward whichever side has room.
  // Horizontal: measure and clamp so the bar/menu never sticks far past
  // the canvas edges.
  const barRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [barShift, setBarShift] = useState(0);
  const [menuShift, setMenuShift] = useState(0);
  const barBelow = el.y < 10;
  const menuUp = !barBelow && el.y > 55;

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const cw = canvas.getBoundingClientRect().width;
    const center = ((el.x + el.w / 2) / 100) * cw;
    const shiftFor = (node: HTMLElement | null): number => {
      if (!node) return 0;
      const half = node.getBoundingClientRect().width / 2;
      return Math.round(clamp(center, half + 6, cw - half - 6) - center);
    };
    setBarShift(shiftFor(barRef.current));
    setMenuShift(shiftFor(menuRef.current));
  }, [menu, el.x, el.w, canvasRef]);

  const rectPct = (dxPx: number, dyPx: number): [number, number] => {
    const r = canvasRef.current?.getBoundingClientRect();
    if (!r) return [0, 0];
    return [(dxPx / r.width) * 100, (dyPx / r.height) * 100];
  };

  const onResize = (e: ReactPointerEvent, corner: string) => {
    e.stopPropagation();
    const sx = e.clientX;
    const sy = e.clientY;
    const o = { x: el.x, y: el.y, w: el.w, h: el.h };
    const move = (ev: PointerEvent) => {
      const [dx, dy] = rectPct(ev.clientX - sx, ev.clientY - sy);
      let { x, y, w, h } = o;
      if (corner.includes('e')) w = o.w + dx;
      if (corner.includes('w')) {
        w = o.w - dx;
        x = o.x + dx;
      }
      if (corner.includes('s')) h = o.h + dy;
      if (corner.includes('n')) {
        h = o.h - dy;
        y = o.y + dy;
      }
      if (w < 6) {
        w = 6;
        x = o.x;
      }
      if (h < 5) {
        h = 5;
        y = o.y;
      }
      if (el.type === 'text') {
        // Text boxes resize within the print-safe area only.
        const kind = store.current?.kind ?? 'spread';
        updateEl(
          el.id,
          clampTextBoxToSafeArea(
            { x, y, w, h },
            store.formatPreset,
            kind,
            textOutsetPct(el, store.formatPreset, kind, SPREAD_TEXT_PANEL_PAD),
          ),
        );
      } else {
        updateEl(el.id, { x, y, w, h });
      }
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const handles = ['nw', 'ne', 'sw', 'se'] as const;
  const isRefining = refiningElId === el.id;
  const toggleMenu = (m: Exclude<OpenMenu, null>) => setMenu((v) => (v === m ? null : m));

  // "Shrink to sharp": scale the box down around its center until the
  // image prints sharp at its pixel count — the only lossless fix for a
  // soft placement (regenerating changes the art).
  const sharpness = useSharpness(el, store);
  const shrinkToSharp = () => {
    if (!sharpness) return;
    const f = shrinkToSharpFactor(sharpness.dpi);
    if (f >= 1) return;
    const w = Math.max(6, el.w * f);
    const h = Math.max(5, el.h * f);
    updateEl(el.id, {
      x: el.x + (el.w - w) / 2,
      y: el.y + (el.h - h) / 2,
      w,
      h,
    });
  };

  const menuClass = `cel__menu ${
    barBelow ? 'cel__menu--below-bar' : menuUp ? 'cel__menu--above' : 'cel__menu--over'
  }`;
  const menuStyle: CSSProperties = {
    transform: `translateX(calc(-50% + ${menuShift}px))`,
  };

  return (
    <div
      className="cel-chrome"
      style={{
        left: `${el.x}%`,
        top: `${el.y}%`,
        width: `${el.w}%`,
        height: `${el.h}%`,
      }}
    >
      <span className="cel-chrome__outline" />
      {handles.map((c) => (
        <span key={c} className={`cel__h cel__h--${c}`} onPointerDown={(e) => onResize(e, c)} />
      ))}
      <div
        className={`cel__bar ${barBelow ? 'cel__bar--below' : ''}`}
        ref={barRef}
        style={{ transform: `translateX(calc(-50% + ${barShift}px))` }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        {el.type === 'text' && (
          <>
            <button
              type="button"
              className="cel__bar-btn"
              title="Smaller text"
              aria-label="Smaller text"
              onClick={() => updateEl(el.id, { size: Math.max(10, el.size - 2) })}
            >
              <span className="cel__bar-btn__glyph" aria-hidden>A−</span>
              <span className="cel__bar-btn__label">Smaller</span>
            </button>
            <button
              type="button"
              className="cel__bar-btn"
              title="Bigger text"
              aria-label="Bigger text"
              onClick={() => updateEl(el.id, { size: el.size + 2 })}
            >
              <span className="cel__bar-btn__glyph" aria-hidden>A+</span>
              <span className="cel__bar-btn__label">Bigger</span>
            </button>
            <button
              type="button"
              className="cel__bar-btn"
              title={`Align (${el.align})`}
              onClick={() =>
                updateEl(el.id, {
                  align: el.align === 'left' ? 'center' : el.align === 'center' ? 'right' : 'left',
                })
              }
            >
              <IconAlign />
              <span className="cel__bar-btn__label">Align</span>
            </button>
            <button
              type="button"
              className={`cel__bar-btn ${el.color === 'paper' ? 'is-on' : ''}`}
              title={el.color === 'paper' ? 'Light text (for dark art)' : 'Dark text'}
              onClick={() => updateEl(el.id, { color: el.color === 'paper' ? 'ink' : 'paper' })}
            >
              <IconContrast />
              <span className="cel__bar-btn__label">Contrast</span>
            </button>
            <button
              type="button"
              className={`cel__bar-btn ${
                menu === 'effect' || (el.effect ?? 'none') !== 'none' ? 'is-on' : ''
              }`}
              title="Readability on art"
              aria-label="Readability on art"
              onClick={() => toggleMenu('effect')}
            >
              <IconReadable />
              <span className="cel__bar-btn__label">Readable</span>
            </button>
            <button
              type="button"
              className={`cel__bar-btn ${menu === 'refine' ? 'is-on' : ''}`}
              title="Refine text with AI"
              aria-label="Refine text with AI"
              onClick={() => toggleMenu('refine')}
              disabled={isRefining}
            >
              {isRefining ? <Spinner size="sm" /> : <IconWand />}
              <span className="cel__bar-btn__label">Refine</span>
            </button>
            <span className="cel__bar-sep" />
          </>
        )}
        {el.type === 'image' && (
          <>
            <button
              type="button"
              className={`cel__bar-btn ${
                menu === 'edge' || (el.edge ?? 'sharp') !== 'sharp' ? 'is-on' : ''
              }`}
              title="Soften edges"
              aria-label="Soften edges"
              onClick={() => toggleMenu('edge')}
            >
              <IconEdges />
              <span className="cel__bar-btn__label">Edges</span>
            </button>
            <button
              type="button"
              className={`cel__bar-btn ${menu === 'shape' || el.shape ? 'is-on' : ''}`}
              title="Cutout shape"
              aria-label="Cutout shape"
              onClick={() => toggleMenu('shape')}
            >
              <IconShape />
              <span className="cel__bar-btn__label">Shape</span>
            </button>
            <button
              type="button"
              className={`cel__bar-btn ${el.crop ? 'is-on' : ''}`}
              title="Crop image"
              aria-label="Crop image"
              onClick={() => {
                setMenu(null);
                setCropOpen(true);
              }}
            >
              <IconCrop />
              <span className="cel__bar-btn__label">Crop</span>
            </button>
            <button
              type="button"
              className={`cel__bar-btn ${el.text_wrap ? 'is-on' : ''}`}
              title={
                el.text_wrap
                  ? 'Text flows around this image'
                  : 'Text overlaps this image'
              }
              aria-label="Text wrap"
              onClick={() => updateEl(el.id, { text_wrap: !el.text_wrap })}
            >
              <IconTextFlow />
              <span className="cel__bar-btn__label">Wrap</span>
            </button>
            {sharpness && (
              <button
                type="button"
                className={`cel__bar-btn cel__bar-btn--shrink cel__bar-btn--shrink-${sharpness.level}`}
                title="Shrink this image to the largest size that still prints sharp"
                data-testid="shrink-to-sharp"
                onClick={shrinkToSharp}
              >
                Shrink to sharp
              </button>
            )}
            <span className="cel__bar-sep" />
          </>
        )}
        <button
          type="button"
          className="cel__bar-btn"
          title="Bring forward"
          aria-label="Bring forward"
          onClick={() => reorderEl(el.id, 1)}
        >
          <IconForward />
          <span className="cel__bar-btn__label">Forward</span>
        </button>
        <button
          type="button"
          className="cel__bar-btn"
          title="Send backward"
          aria-label="Send backward"
          onClick={() => reorderEl(el.id, -1)}
        >
          <IconBackward />
          <span className="cel__bar-btn__label">Backward</span>
        </button>
        <button type="button" className="cel__bar-btn" title="Duplicate" onClick={() => duplicateEl(el.id)}>
          <IconCopy />
          <span className="cel__bar-btn__label">Duplicate</span>
        </button>
        <button
          type="button"
          className="cel__bar-btn cel__bar-btn--danger"
          title="Delete"
          onClick={() => removeEl(el.id)}
        >
          <IconTrash />
          <span className="cel__bar-btn__label">Delete</span>
        </button>
      </div>

      {el.type === 'text' && menu === 'refine' && (
        <div
          className={`${menuClass} cel__refine-menu`}
          ref={menuRef}
          style={menuStyle}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {TEXT_REFINE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className="cel__menu-item cel__refine-item"
              disabled={isRefining}
              onClick={() => {
                setMenu(null);
                void refineText(el.id, opt.value);
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {el.type === 'text' && menu === 'effect' && (
        <div
          className={menuClass}
          ref={menuRef}
          style={menuStyle}
          data-testid="text-effect-menu"
          onPointerDown={(e) => e.stopPropagation()}
        >
          {TEXT_EFFECT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`cel__menu-item ${
                (el.effect ?? 'none') === opt.value ? 'cel__menu-item--on' : ''
              }`}
              onClick={() => {
                setMenu(null);
                updateEl(el.id, { effect: opt.value });
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {el.type === 'image' && menu === 'edge' && (
        <div
          className={`${menuClass} cel__menu--edges`}
          ref={menuRef}
          style={menuStyle}
          data-testid="image-edge-menu"
          onPointerDown={(e) => e.stopPropagation()}
        >
          {IMAGE_EDGE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              title={opt.hint}
              className={`edgeopt ${
                (el.edge ?? 'sharp') === opt.value ? 'edgeopt--on' : ''
              }`}
              onClick={() => {
                setMenu(null);
                // An edge treatment stacks with a cutout shape — the
                // softening applies to the cut silhouette.
                updateEl(el.id, { edge: opt.value });
              }}
            >
              <span
                className="edgeopt__prev"
                aria-hidden
                style={
                  el.shape
                    ? imageShapeCss(el.shape, EDGE_PREVIEW_AR, opt.value)
                    : imageEdgeCss(opt.value, EDGE_PREVIEW_AR, '7px')
                }
              />
              <span className="edgeopt__label">{opt.label}</span>
            </button>
          ))}
        </div>
      )}

      {el.type === 'image' && menu === 'shape' && (
        <div
          className={`${menuClass} cel__menu--edges`}
          ref={menuRef}
          style={menuStyle}
          data-testid="image-shape-menu"
          onPointerDown={(e) => e.stopPropagation()}
        >
          {IMAGE_SHAPE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              title={opt.hint}
              className={`edgeopt ${
                (el.shape?.kind ?? 'none') === opt.value ? 'edgeopt--on' : ''
              }`}
              onClick={() => pickShape(opt.value)}
            >
              <span
                className="edgeopt__prev"
                aria-hidden
                style={
                  opt.value === 'none'
                    ? imageEdgeCss(el.edge ?? 'sharp', EDGE_PREVIEW_AR, '7px')
                    : imageShapeCss(
                        { kind: opt.value, ...DEFAULT_SHAPE_TRANSFORM },
                        EDGE_PREVIEW_AR,
                        el.edge ?? 'sharp',
                      )
                }
              />
              <span className="edgeopt__label">{opt.label}</span>
            </button>
          ))}
          {el.shape && (
            <button
              type="button"
              className="cel__menu-item"
              onClick={() => {
                setMenu(null);
                setShapeAdjust(true);
              }}
            >
              Adjust placement…
            </button>
          )}
        </div>
      )}

      {shapeAdjust && el.type === 'image' && el.shape && (
        <ShapeOverlay
          el={el}
          shape={el.shape}
          boxAspect={(el.w / el.h) * store.canvasAspect}
          onChange={(shape) => updateEl(el.id, { shape })}
          onClose={() => setShapeAdjust(false)}
        />
      )}

      {cropOpen && el.type === 'image' && (
        <CropOverlay
          src={r2Url(el.r2_key)}
          caption={el.caption}
          initial={el.crop ?? null}
          onApply={(crop) => {
            updateEl(el.id, { crop });
            setCropOpen(false);
          }}
          onClose={() => setCropOpen(false)}
        />
      )}
    </div>
  );
}

interface SpreadCanvasProps {
  store: ComposeStore;
  bookFont: BookFont;
}

export function SpreadCanvas({ store, bookFont }: SpreadCanvasProps) {
  const { current, selectedEl, setSelectedEl, canvasAspect } = store;
  const ref = useRef<HTMLDivElement | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Selecting a different element (or nothing) ends any in-place edit.
  useEffect(() => {
    setEditingId((cur) => (cur && cur !== selectedEl ? null : cur));
  }, [selectedEl]);

  if (!current) return null;

  const isSingle = current.kind === 'cover' || current.kind === 'back';
  const els = sortedElements(current.elements);
  const activeEl = current.elements.find((e) => e.id === selectedEl) ?? null;

  return (
    <div className="spread-wrap">
      <div
        className={`spread spread--editor ${isSingle ? 'spread--single' : ''}`}
        style={{ ['--spread-ar' as string]: canvasAspect }}
        data-spread-canvas
        data-testid="spread-canvas"
        ref={ref}
        onPointerDown={() => setSelectedEl(null)}
      >
        {/* Page content clips at the paper edge (art dragged past the edge
            crops like the PDF); the chrome below lives OUTSIDE this layer
            so outline/handles/toolbar stay visible past the edge. */}
        <div className="spread__clip">
          {!isSingle && <div className="spread__gutter" />}
          {els.map((el) => (
            <CanvasElement
              key={el.id}
              el={el}
              active={selectedEl === el.id}
              editing={editingId === el.id}
              onStartEditing={setEditingId}
              onEndEditing={() => setEditingId(null)}
              canvasRef={ref}
              store={store}
              bookFont={bookFont}
            />
          ))}
          {current.elements.length === 0 && (
            <div className="spread__empty">
              <IconImage />
              <span>Drag images from the bank, or add a text block</span>
            </div>
          )}
        </div>
        {activeEl && editingId !== activeEl.id && (
          <ElementChrome key={activeEl.id} el={activeEl} canvasRef={ref} store={store} />
        )}
      </div>
    </div>
  );
}

/**
 * Read-only spread render for the Preview overlay and the Export cover
 * preview (no handles). Mirrors the PDF renderer's extras so previews
 * show the exported book, not just the raw spread:
 *   • `folios` prints the physical page numbers the PDF prints (10pt,
 *     centered at 36pt from the bottom edge of each page),
 *   • an empty cover/back renders the same fallback the PDF emits
 *     (centered title / "Made with HistoRyjki").
 */
export function StaticSpread({
  spread,
  aspect,
  bookFont,
  formatPreset,
  folios,
  fallbackTitle,
}: {
  spread: SpreadView;
  aspect: number;
  bookFont: BookFont;
  /** Needed to place folios/fallbacks at the PDF's point-true positions. */
  formatPreset?: FormatPreset | null;
  /** Physical [left, right] page numbers to print on an interior spread. */
  folios?: [number, number] | null;
  /** Book title for the PDF's empty-cover fallback. */
  fallbackTitle?: string;
}) {
  const isSingle = spread.kind === 'cover' || spread.kind === 'back';
  const els = sortedElements(spread.elements);
  const { widthPt, heightPt } = getCanvasSizePt(formatPreset ?? null, spread.kind);
  // 1pt of the physical canvas, as a percentage of rendered canvas width —
  // lets point-sized PDF details (folios, fallback type) scale correctly.
  const ptCqw = 100 / widthPt;
  const folioBottomPct = (36 / heightPt) * 100;

  const fallback =
    els.length === 0 && spread.kind === 'cover' ? (
      <div
        className="spread__fallback spread__fallback--cover"
        style={{ fontFamily: bookFont.family, fontWeight: 700, fontSize: `${36 * ptCqw}cqw` }}
      >
        {fallbackTitle || 'Untitled Story'}
      </div>
    ) : els.length === 0 && spread.kind === 'back' ? (
      <div
        className="spread__fallback"
        style={{ fontFamily: bookFont.family, fontSize: `${18 * ptCqw}cqw` }}
      >
        Made with HistoRyjki
      </div>
    ) : null;

  return (
    <div
      className={`spread ${isSingle ? 'spread--single' : ''}`}
      style={{ ['--spread-ar' as string]: aspect }}
    >
      {!isSingle && <div className="spread__gutter" />}
      {fallback}
      {folios && (
        <>
          <span
            className="spread__folio"
            style={{
              left: '25%',
              bottom: `${folioBottomPct}%`,
              fontFamily: bookFont.family,
              fontSize: `${10 * ptCqw}cqw`,
            }}
          >
            {folios[0]}
          </span>
          <span
            className="spread__folio"
            style={{
              left: '75%',
              bottom: `${folioBottomPct}%`,
              fontFamily: bookFont.family,
              fontSize: `${10 * ptCqw}cqw`,
            }}
          >
            {folios[1]}
          </span>
        </>
      )}
      {els.map((el) => (
        <div
          key={el.id}
          className={`cel cel--${el.type} cel--static ${softEdgeClass(el)}`}
          style={{
            left: `${el.x}%`,
            top: `${el.y}%`,
            width: `${el.w}%`,
            height: `${el.h}%`,
            zIndex: el.z,
          }}
        >
          {el.type === 'image' ? (
            <SpreadImage el={el} boxAspect={(el.w / el.h) * aspect} />
          ) : (
            <>
              <TextPanel el={el} />
              <FlowedText
                el={el}
                elements={spread.elements}
                canvasAspect={aspect}
                bookFont={bookFont}
              />
            </>
          )}
        </div>
      ))}
    </div>
  );
}
